import { XMarkIcon } from '@heroicons/react/24/outline'
import { Form, Formik } from 'formik'
import { get } from 'lodash'
import { FormattedMessage, useIntl } from 'react-intl'

import Modal from '@/components/Modal'
import Button from '@/components/TailwindUI/Button'
import TextInput from '@/components/TailwindUI/TextInput'

import { partialChannelConnectionSchema } from './channelConnectionSchema'
import messages from './messages'

const initialValues = {
  name: '',
  claimValueTenantId: '',
  type: 'oidc',
  jwksUri: ''
}

interface CreateChannelConnectionModalProps {
  appId?: string
  isEditing?: boolean
  isOpen?: boolean
  formValues?: Partial<typeof initialValues>
  onSubmit: (values: typeof initialValues) => void
  onClose: () => void
}

const CreateChannelConnectionModal = ({
  isOpen = true,
  isEditing = false,
  onSubmit,
  onClose,
  formValues = {}
}: CreateChannelConnectionModalProps) => {
  const intl = useIntl()

  const initialChannelConnectionValues = { ...initialValues, ...formValues }

  const handleCancel = (reset: Function) => {
    onClose()
    reset()
  }

  return (
    <Formik
      enableReinitialize
      onSubmit={values => {
        onSubmit(values)
      }}
      validationSchema={partialChannelConnectionSchema(intl)}
      initialValues={initialChannelConnectionValues}
    >
      {({ resetForm, values, errors, touched, setFieldValue }) => {
        return (
          <Modal isOpen={isOpen} onClose={onClose}>
            <div>
              <div className="absolute right-0 top-0 hidden pr-4 pt-4 sm:block">
                <button
                  type="button"
                  className="rounded-md bg-white text-gray-400 hover:text-gray-500"
                  onClick={() => handleCancel(resetForm)}
                >
                  <span className="sr-only">Close</span>
                  <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                </button>
              </div>
              <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                <h3 className="text-xl font-semibold leading-6 text-gray-900">
                  {isEditing ? (
                    <FormattedMessage {...messages.editTitle} />
                  ) : (
                    <FormattedMessage {...messages.createTitle} />
                  )}
                </h3>
              </div>
              <Form className="sm:flex sm:items-start">
                <div className="sm: mt-3 mr-4 flex-auto text-center sm:ml-4 sm:mt-0 sm:text-left">
                  <div className="pt-8 leading-6">
                    <div className="col-span-full">
                      <TextInput
                        name="name"
                        label={intl.formatMessage(messages.channelConnectionNameLabel)}
                        boldHeaders={true}
                        value={get(values, `name`)}
                        required={true}
                        placeholder={intl.formatMessage(messages.channelConnectionNamePlaceholder)}
                        onChange={e => setFieldValue('name', e.target.value)}
                        error={touched.name && errors.name ? errors.name.toString() : ''}
                      />
                    </div>
                  </div>
                  {isEditing ? (
                    <div className="pt-8 leading-6">
                      <div className="col-span-full">
                        <TextInput
                          name="type"
                          label={intl.formatMessage(messages.channelConnectionChannelLabelEditing)}
                          boldHeaders={true}
                          value={
                            get(values, 'type') === 'oidc'
                              ? intl.formatMessage(messages.oidcLabel)
                              : get(values, 'type')
                          } // leave fallback for now
                          readOnly={true}
                        />
                      </div>
                    </div>
                  ) : (
                    <div />
                  )}

                  <div className="pt-8 leading-6">
                    <div className="col-span-full">
                      <TextInput
                        name="claimValueTenantId"
                        label={intl.formatMessage(messages.channelConnectionIdOIDCLabel)}
                        boldHeaders={true}
                        description={intl.formatMessage(messages.channelConnectionIdOIDCHint)}
                        descriptionLocation={'before'}
                        value={get(values, 'claimValueTenantId')}
                        required={true}
                        placeholder={intl.formatMessage(messages.channelConnectionIdPlaceholder)}
                        onChange={e => setFieldValue('claimValueTenantId', e.target.value)}
                        error={
                          touched.claimValueTenantId && errors.claimValueTenantId
                            ? errors.claimValueTenantId.toString()
                            : ''
                        }
                        readOnly={isEditing}
                      />
                    </div>
                  </div>

                  <div className="pt-8 leading-6">
                    <div className="col-span-full">
                      <TextInput
                        name="jwksUri"
                        label={intl.formatMessage(messages.channelConnectionJWKSURILabel)}
                        boldHeaders={true}
                        value={get(values, 'jwksUri')}
                        required={!isEditing}
                        placeholder={intl.formatMessage(messages.channelConnectionJWKSURIPlaceholder)}
                        onChange={e => setFieldValue('jwksUri', e.target.value)}
                        error={touched.jwksUri && errors.jwksUri ? errors.jwksUri.toString() : ''}
                        readOnly={isEditing}
                      />
                    </div>
                  </div>

                  <div className="mt-12 flex items-center justify-end gap-x-2">
                    <Button color="secondary" onClick={() => handleCancel(resetForm)}>
                      <FormattedMessage {...messages.cancel} />
                    </Button>
                    <Button type="submit" color="primary">
                      {isEditing ? (
                        <FormattedMessage {...messages.update} />
                      ) : (
                        <FormattedMessage {...messages.create} />
                      )}
                    </Button>
                  </div>
                </div>
              </Form>
            </div>
          </Modal>
        )
      }}
    </Formik>
  )
}

export default CreateChannelConnectionModal
